import React, { CSSProperties, useContext } from 'react';
import { Form, Input } from 'antd';
import { FormInstance } from 'antd/es/form';
import NumericKeyboard, { IsolateKeyboard } from '@/components/NumericKeyboard';
import { PaymentChannel } from '@/models/payment';
import styles from '../style.less';
import { CloseCircleOutlined, PushpinOutlined, WalletOutlined } from '@ant-design/icons';
import Price from '@/components/Price';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { getPlusAmount, RechargeDiscountType } from '@/models/poiConfig';
import BigNumber from 'bignumber.js';
import PosButton from '@/components/PosButton';
import { PreOrder } from '@/models/order';

type Props = {
  form: FormInstance;
  userFullInfo: PreOrder['userFullInfo'];
};

const inlineStyles: { [k: string]: CSSProperties } = {
  balanceCodeWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  balanceCodeLabel: {
    paddingRight: 10,
  },
};

const RechargeTradeArea: React.FC<Props> = ({ form, userFullInfo }: Props) => {
  const [
    {
      poiConfig: { rechargeConfig },
    },
  ] = useContext(BaseInfoContext);

  const hasOpenBalance =
    userFullInfo && userFullInfo.user && userFullInfo.user.balance !== undefined;

  const requireBalanceCode = !hasOpenBalance && userFullInfo;

  return (
    <>
      <div
        className={`${styles.item} ${!userFullInfo ? styles.error : ''} ${
          requireBalanceCode ? styles.warning : ''
        } ${hasOpenBalance ? styles.success : ''}`}>
        <h3 className={`${requireBalanceCode ? '' : styles.single}`}>
          {userFullInfo ? (
            <>
              <WalletOutlined />{' '}
              {hasOpenBalance ? (
                <>
                  当前账户余额: <Price value={userFullInfo.user.balance} />
                </>
              ) : (
                <>当前账户首次充值，请推荐客户设置支付密码</>
              )}
            </>
          ) : (
            <>
              <CloseCircleOutlined /> 充值前请先关联客户
            </>
          )}
        </h3>
        {requireBalanceCode && (
          <div>
            <div style={inlineStyles.balanceCodeWrapper}>
              {/*<div style={inlineStyles.balanceCodeLabel}>账户支付密码:</div>*/}
              <Form.Item
                name={'balanceCode'}
                // noStyle
                extra={<>6位支付密码，该密码将用于余额支付时验证</>}
                rules={[
                  // {
                  //   required: true,
                  //   message: <>为保证客户资金安全, 请为账户设定6位支付密码</>,
                  // },
                  {
                    len: 6,
                    message: <>支付密码须为6位数字</>,
                  },
                ]}>
                <IsolateKeyboard
                  showMask
                  keyboardProps={{
                    maxLength: 6,
                    showAllClean: true,
                  }}
                />
              </Form.Item>
            </div>
          </div>
        )}
      </div>
      <div className={`${styles.item} ${styles.fullSize} overflow-hidden`}>
        <Form.Item noStyle name={'rechargePlusPrice'}>
          <Input type={'hidden'} />
        </Form.Item>
        <Form.Item noStyle name={'actualPrice'}>
          <NumericKeyboard
            className={styles.rechargeKeyboard}
            placeholder={'请输入充值金额'}
            parseValueToNumber
            precision={2}
            onChange={() => {
              let actualPrice = form.getFieldValue('actualPrice');
              if (!actualPrice) {
                form.setFieldsValue({
                  paymentChannel: PaymentChannel.Online,
                });
              } else {
                form.setFieldsValue({
                  rechargePlusPrice: getPlusAmount(rechargeConfig, actualPrice),
                });
              }
            }}
          />
        </Form.Item>
      </div>
      {rechargeConfig.discountType !== RechargeDiscountType.OFF && (
        <div className={styles.item}>
          <h3 style={{ display: 'flex', alignItems: 'center' }}>
            <PushpinOutlined /> 充值优惠
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) =>
                prev.rechargePlusPrice !== cur.rechargePlusPrice ||
                prev.actualPrice !== cur.actualPrice
              }>
              {({ getFieldValue }) => {
                const actualPrice = new BigNumber(getFieldValue('actualPrice'));
                const rechargePlusPrice = getFieldValue('rechargePlusPrice');
                if (actualPrice.isPositive() && !actualPrice.isZero()) {
                  return (
                    <div style={{ fontSize: 14, flex: 1, textAlign: 'right' }}>
                      充 <Price value={actualPrice} /> 送 <Price value={rechargePlusPrice} /> 实得{' '}
                      <Price value={actualPrice.plus(rechargePlusPrice)} type={'green'} />
                    </div>
                  );
                }
              }}
            </Form.Item>
          </h3>

          {rechargeConfig.discountType === RechargeDiscountType.Amount_PLUS_Amount && (
            <div className={styles.rechargeRules}>
              {rechargeConfig.amountPlusAmount.map(item => {
                return (
                  <div key={`${item.matchAmount}-${item.plusAmount}`}>
                    <PosButton
                      type={'dashed'}
                      size={'middle'}
                      onClick={() => {
                        form.setFieldsValue({
                          actualPrice: item.matchAmount,
                          rechargePlusPrice: item.plusAmount,
                        });
                      }}>
                      <>
                        满 <Price value={item.matchAmount} /> 送 <Price value={item.plusAmount} />
                      </>
                    </PosButton>
                  </div>
                );
              })}
            </div>
          )}

          {rechargeConfig.discountType === RechargeDiscountType.Amount_PLUS_Amount_Multi && (
            <>
              <div>
                每满 <Price value={rechargeConfig.amountPlusAmountMulti.perMatchAmount} /> 送{' '}
                <Price value={rechargeConfig.amountPlusAmountMulti.plusAmount} /> 元
                {rechargeConfig.amountPlusAmountMulti.plusLimit > 0 && (
                  <>
                    ，最多送 <Price value={rechargeConfig.amountPlusAmountMulti.plusLimit} /> 元
                  </>
                )}
              </div>
              {rechargeConfig.amountPlusAmountMultiPresets &&
              rechargeConfig.amountPlusAmountMultiPresets.length ? (
                <div className={styles.rechargeRules} style={{ paddingTop: 10 }}>
                  {rechargeConfig.amountPlusAmountMultiPresets.map(amount => {
                    const plusAmount = getPlusAmount(rechargeConfig, amount);
                    return (
                      <div key={`${amount}`}>
                        <PosButton
                          type={'dashed'}
                          size={'middle'}
                          onClick={() => {
                            form.setFieldsValue({
                              actualPrice: amount,
                              rechargePlusPrice: plusAmount,
                            });
                          }}>
                          <>
                            充 <Price value={amount} /> 送 <Price value={plusAmount} />
                          </>
                        </PosButton>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default RechargeTradeArea;
